// FootnoteRefNode — TipTap node for excerptus.footnoteRef.
// v1: block-level; body is rich text (bold / italic / link) edited via popover.
// Numbering happens at render time (renderBriefDocument) — the node only
// stores a stable id + body. Body string carries a whitelisted HTML subset
// (see src/lib/briefDocument/footnoteBody.ts).
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { footnoteBodyId, footnoteRefId } from "@/lib/briefDocument/footnoteAnchors";
import { footnoteBodyPlainText } from "@/lib/briefDocument/footnoteBody";
import { FootnoteBodyEditor } from "./FootnoteBodyEditor";
import { FootnoteAttachSource } from "./FootnoteAttachSource";

const FootnoteRefView = ({ node, updateAttributes }: NodeViewProps) => {
  const [open, setOpen] = useState(false);
  const body = (node.attrs.body as string) ?? "";
  const id = (node.attrs.id as string | null) ?? null;
  const refId = (node.attrs.refId as string | null) ?? null;
  const locator = (node.attrs.locator as string | null) ?? null;
  const preview = footnoteBodyPlainText(body).slice(0, 60);
  const jumpToBody = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!id) return;
    document.getElementById(footnoteBodyId(id))?.scrollIntoView({ behavior: "smooth", block: "center" });
    setOpen(false);
  };
  return (
    <NodeViewWrapper as="span" className="inline-block">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id ? footnoteRefId(id) : undefined}
            className="mx-0.5 inline-flex items-center rounded bg-primary/10 px-1.5 py-0.5 align-super text-xs font-medium text-primary hover:bg-primary/20 scroll-mt-24"
            aria-label="Edit footnote"
          >
            [fn{preview ? `: ${preview}${preview.length === 60 ? "…" : ""}` : ""}]
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-96 space-y-3">
          <FootnoteAttachSource
            refId={refId}
            locator={locator}
            onChange={(next) => updateAttributes(next)}
          />
          <div>
            <Label className="mb-2 block text-xs">Footnote body</Label>
            <FootnoteBodyEditor
              value={body}
              onChange={(next) => updateAttributes({ body: next })}
              ariaLabel="Footnote body"
              placeholder={
                refId
                  ? "Optional commentary appended after the citation (e.g. \"See also chapter 3…\")."
                  : undefined
              }
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] text-muted-foreground">
              Numbering follows document order.
            </p>
            {id ? (
              <a
                href={`#${footnoteBodyId(id)}`}
                onClick={jumpToBody}
                className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Jump to body ↓
              </a>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};

export const FootnoteRefNode = Node.create({
  name: "excerptus.footnoteRef",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-id"),
        renderHTML: (attrs) => ({ "data-id": attrs.id }),
      },
      body: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-body") ?? "",
        renderHTML: (attrs) => ({ "data-body": attrs.body }),
      },
      /**
       * Optional library_items.id this footnote cites. When set, the
       * citations preview re-renders the body via the Chicago short-note /
       * Ibid sequence rules instead of using the literal `body` text.
       * Back-compat: existing nodes without this attr keep their stored body.
       */
      refId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-ref-id"),
        renderHTML: (attrs) => (attrs.refId ? { "data-ref-id": attrs.refId as string } : {}),
      },
      /** Optional locator (e.g. "p. 42") paired with refId. */
      locator: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-locator"),
        renderHTML: (attrs) => (attrs.locator ? { "data-locator": attrs.locator as string } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span.footnote-ref" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes, { class: "footnote-ref" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(FootnoteRefView);
  },
});
